import React, { useMemo, useRef, useState, useLayoutEffect, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'
import { Navigation, Pagination } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'

import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
import 'swiper/css/scrollbar'

import { FacebookPreview } from 'components/Recommendations/EventCampaigns/Steps/Setup/Summary/Previews/V2/FacebookPreview'

import eventEvaluationApi from 'domain/eventEvaluation/api'
import ReelsPreview from 'components/Recommendations/EventCampaigns/Steps/Setup/Summary/Previews/V2/ReelsPreview'
import InstagramPreview from 'components/Recommendations/EventCampaigns/Steps/Setup/Summary/Previews/V2/InstagramPreview'
import FacebookRightColumnPreview from 'components/Recommendations/EventCampaigns/Steps/Setup/Summary/Previews/V2/FacebookRightColumnPreview'
import DisplayAdCard from 'components/Recommendations/EventCampaigns/Steps/Setup/Summary/PreviewAdsModal/AdCards/GoogleDisplay'
import LoadingSpinner from 'components/Shared/LoadingSpinner'
import PlatformToggle from 'components/Shared/PlatformToggle'
import TableMessage from 'components/Shared/TableMessage'
import AccordionCard from 'components/Shared/AccordionCard'
import { resolveCallToAction } from 'utils/helpers'
import { getGoogleTopAdPreviews } from 'domain/adPreview/googleTopAdVariations'

const AdPreviewCard = ({ ad, onLoaded }) => {
  const renderPreview = () => {
    switch (ad.type) {
      case 'facebook_web':
        return (
          <FacebookPreview
            data={ad.data}
            onLoaded={onLoaded}
            placement={ad.placement}
            label={ad.label}
          />
        )
      case 'facebook_mobile':
        return (
          <FacebookPreview
            data={ad.data}
            mobileView
            onLoaded={onLoaded}
            placement={ad.placement}
            label={ad.label}
          />
        )
      case 'instagram':
        return (
          <InstagramPreview
            data={ad.data}
            onLoaded={onLoaded}
            placement={ad.placement}
            label={ad.label}
          />
        )
      case 'reel':
        return (
          <ReelsPreview
            data={ad.data}
            onLoaded={onLoaded}
            placement={ad.placement}
            label={ad.label}
          />
        )
      case 'facebook_right_column':
        return (
          <FacebookRightColumnPreview data={ad.data} placement={ad.placement} label={ad.label} />
        )
      default:
        return null
    }
  }

  return <div className="feature-preview-card">{renderPreview()}</div>
}

AdPreviewCard.propTypes = {
  ad: PropTypes.object.isRequired,
  onLoaded: PropTypes.func,
}

// v4's mapTopPerformingAds emits camelCase (destinationUrl/callToAction),
// but FacebookPreview/InstagramPreview/ReelsPreview/FacebookRightColumnPreview
// all still destructure the old snake_case field names — adapt at the
// boundary here rather than touching 4 preview components.
function adaptTopAd(item) {
  return {
    ...item,
    destination_url: item.destinationUrl,
    call_to_action: item.callToAction,
  }
}

// A Google top-ad's `media` is a raw, unvalidated passthrough of whatever
// the creative decode step found — usually a flat {url, width, height,
// type} item, but for Demand Gen multi-asset ads it's a bundle
// ({marketing_image, square_marketing_image, ...}, no `url` of its own).
// Unwrap the bundle to the first available sub-image rather than handing
// DisplayAdCard something it can't render.
export function extractGoogleMedia(media) {
  if (!media) return null
  if (media.url) return media
  const bundleImage =
    media.marketing_image ??
    media.square_marketing_image ??
    media.portrait_marketing_image ??
    media.tall_portrait_marketing_image
  return bundleImage?.url ? bundleImage : null
}

// Google top-ad items frequently have every creative field null (the
// variation_id decode this depends on server-side is best-effort) — render
// a full DisplayAdCard only when there's an actual image to show.
export function hasGoogleCreative(item) {
  return Boolean(
    extractGoogleMedia(item.media) || item.headline || item.description || item.longHeadline,
  )
}

export function mapGoogleTopAdToDisplayAd(item, t) {
  return {
    skin: 'bare-stacked',
    headline: item.headline ?? '',
    longHeadline: item.longHeadline ?? '',
    description: item.description ?? '',
    callToAction:
      resolveCallToAction(item.callToAction, 'google') ||
      t('Recommendations.campaign.setup.call_to_action.LEARN_MORE'),
    destinationUrl: item.destinationUrl ?? '',
    displayUrlPath: null,
    businessName: '',
    logo: null,
    media: extractGoogleMedia(item.media),
  }
}

const GoogleTopAdMinimalCard = ({ ad }) => {
  const { t } = useTranslation()
  return (
    <div className="google-display-card google-display-card--minimal">
      <div className="google-display-card__panel">
        <div className="google-display-card__headline">{ad.name}</div>
        {ad.adScore != null && (
          <div className="google-display-card__description">
            {t('Dashboard.bestPerformingAds.adScore', { score: ad.adScore })}
          </div>
        )}
      </div>
    </div>
  )
}

GoogleTopAdMinimalCard.propTypes = {
  ad: PropTypes.shape({
    name: PropTypes.string.isRequired,
    adScore: PropTypes.number,
  }).isRequired,
}

function adNameLabel(name) {
  // e.g. partner-id-ft_event-sim-12648_Peru_v11111 -> Peru_v11111
  const nameParts = (name ?? '').split('_')
  return nameParts.length > 1 ? nameParts.slice(-2).join('_') : name
}

// A Google ad runs on every surface its ad type serves, so show one slide per
// placement its assets can fill. Carousel ads and undecoded rows get one card.
function buildGooglePreviews(item, t) {
  const previews = getGoogleTopAdPreviews(item, t)
  if (previews.length) {
    return previews.map((preview) => ({ ...preview, item }))
  }
  return [
    {
      key: 'single',
      placement: null,
      ad: hasGoogleCreative(item) ? mapGoogleTopAdToDisplayAd(item, t) : null,
      item,
    },
  ]
}

function generatePlacementsForAd(ad) {
  const placements = []
  const { media } = ad
  const rAspectRatio = media ? media.height / media.width : 1
  const isReel = rAspectRatio > 1.4
  const isTinyMedia = media ? media.width < 320 && media.height < 200 : false
  const label = adNameLabel(ad.name)
  // Facebook Feed Desktop
  if (!isReel) {
    placements.push({
      type: 'facebook_web',
      data: { ...ad },
      placement: 'Facebook feed desktop',
      label: label,
    })
    // Facebook Feed Mobile
    placements.push({
      type: 'facebook_mobile',
      data: { ...ad },
      placement: 'Facebook feed mobile',
      label: label,
    })
    // Instagram Feed
    placements.push({
      type: 'instagram',
      data: { ...ad },
      placement: 'Instagram feed',
      label: label,
    })
    // Facebook Right Column
    if (isTinyMedia) {
      placements.push({
        type: 'facebook_right_column',
        data: { ...ad },
        placement: 'Facebook right column',
        label: label,
      })
    }
  } else {
    placements.push({
      type: 'reel',
      data: { ...ad },
      placement: 'Instagram reels',
      label: label,
    })
  }

  return placements
}

// Accept eventId as a prop for clarity
const BestPerformingAds = ({ eventId }) => {
  const { t } = useTranslation()
  const swiperRef = useRef(null)
  const prevRef = useRef(null)
  const nextRef = useRef(null)
  const containerRef = useRef(null)
  const CARD_WIDTH_BASE = 305
  const CARD_WIDTH = CARD_WIDTH_BASE + 55 // (55 extra padding)
  const [slidesPerView, setSlidesPerView] = useState(3)
  const [metaAds, setMetaAds] = useState([])
  const [googleAds, setGoogleAds] = useState([])
  const [loading, setLoading] = useState(true)
  const [platform, setPlatform] = useState('all')
  const [isAccordionOpen, setIsAccordionOpen] = useState(true)

  const handlePlatformChange = (newPlatform) => {
    setPlatform(newPlatform)
    setIsAccordionOpen(true)
  }

  useEffect(() => {
    let isMounted = true
    if (!eventId) return
    setLoading(true)
    eventEvaluationApi.fetchTopPerformingAdsAsync(eventId, { platform, limit: 5 }).then((res) => {
      if (!isMounted) return
      // Only the requested platform's key is present on the response (a
      // platform=google request omits `meta` entirely) — default both to
      // [] the same way, rather than treating "absent" as an error state.
      setMetaAds((res.data?.meta ?? []).map(adaptTopAd))
      setGoogleAds(res.data?.google ?? [])
      setLoading(false)
    })
    return () => {
      isMounted = false
    }
  }, [eventId, platform])

  // Flatten all Meta placements for all ads, in order, then all Google
  // placement previews the same way.
  const metaPreviews = metaAds.flatMap(generatePlacementsForAd)
  const googlePreviews = googleAds.flatMap((ad) => buildGooglePreviews(ad, t))
  const previewsLength = metaPreviews.length + googlePreviews.length

  // Dynamically calculate slidesPerView based on container width
  useLayoutEffect(() => {
    let debounceTimer = null
    function updateSlidesPerView() {
      if (containerRef.current) {
        const containerWidth = containerRef.current.offsetWidth
        const maxSlides = Math.max(1, Math.floor(containerWidth / CARD_WIDTH))
        setSlidesPerView(Math.min(maxSlides, previewsLength || 3))
      }
    }
    function debouncedUpdate() {
      if (debounceTimer) clearTimeout(debounceTimer)
      debounceTimer = setTimeout(updateSlidesPerView, 100)
    }
    // Use requestAnimationFrame to ensure DOM is painted
    const raf = requestAnimationFrame(updateSlidesPerView)
    window.addEventListener('resize', debouncedUpdate)
    return () => {
      window.removeEventListener('resize', debouncedUpdate)
      cancelAnimationFrame(raf)
      if (debounceTimer) clearTimeout(debounceTimer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewsLength])

  const swiperWidth = useMemo(() => {
    return slidesPerView * 305 + (slidesPerView - 1) * 16
  }, [slidesPerView])

  useEffect(() => {
    if (
      swiperRef.current &&
      prevRef.current &&
      nextRef.current &&
      swiperRef.current.params.navigation
    ) {
      swiperRef.current.params.navigation.prevEl = prevRef.current
      swiperRef.current.params.navigation.nextEl = nextRef.current
      swiperRef.current.navigation.destroy()
      swiperRef.current.navigation.init()
      swiperRef.current.navigation.update()
    }
  }, [previewsLength, slidesPerView, prevRef.current, nextRef.current])

  return (
    <div className="feature-performing-ads event-series-stats">
      <AccordionCard
        isOpen={isAccordionOpen}
        onToggle={setIsAccordionOpen}
        title={
          <div className="header-with-platform-toggle">
            <h2 className="card-title-default d-inline-block mb-0">
              {t('Dashboard.bestPerformingAds.title')}
            </h2>
            <PlatformToggle
              platform={platform}
              setPlatform={handlePlatformChange}
              disabled={loading}
            />
          </div>
        }
      >
        {loading && (
          <div className="text-center w-100">
            <LoadingSpinner showText title={t('common.loading')}></LoadingSpinner>
          </div>
        )}
        {!loading && !previewsLength && (
          <TableMessage variant="empty" description={t('Dashboard.bestPerformingAds.noAds')} />
        )}
        {!loading && !!previewsLength && (
          <div className="feature-carousel-container" ref={containerRef}>
            <button
              ref={prevRef}
              className="feature-carousel-button feature-carousel-button-prev"
              onClick={() => swiperRef.current.slidePrev()}
            >
              <i class="fa-solid fa-chevron-left"></i>
            </button>
            <div
              style={{
                width: swiperWidth,
                margin: '0 auto',
              }}
            >
              <Swiper
                onSwiper={(swiper) => (swiperRef.current = swiper)}
                modules={[Navigation, Pagination]}
                spaceBetween={16}
                slidesPerView={slidesPerView}
                className="feature-swiper"
                style={{
                  '--swiper-navigation-size': '18px',
                  '--swiper-navigation-color': '#007bff',
                }}
              >
                {metaPreviews.map((ad, index) => (
                  <SwiperSlide
                    key={`meta-${ad.data.id}-${index}`}
                    className="feature-swiper-slide"
                    style={{ width: 305 }}
                  >
                    <AdPreviewCard ad={ad} />
                  </SwiperSlide>
                ))}
                {googlePreviews.map((preview, index) => (
                  <SwiperSlide
                    key={`google-${preview.item.id}-${preview.key}-${index}`}
                    className="feature-swiper-slide"
                    style={{ width: 305 }}
                  >
                    <div className="feature-preview-card feature-preview-card--google">
                      {preview.ad ? (
                        <DisplayAdCard ad={preview.ad} />
                      ) : (
                        <GoogleTopAdMinimalCard ad={preview.item} />
                      )}
                      <div className="feature-card-label">
                        {preview.placement && <span>{preview.placement}</span>}
                        <span>{adNameLabel(preview.item.name)}</span>
                      </div>
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
            <button
              ref={nextRef}
              className="feature-carousel-button feature-carousel-button-next"
              onClick={() => swiperRef.current.slideNext()}
            >
              <i class="fa-solid fa-chevron-right"></i>
            </button>
          </div>
        )}
      </AccordionCard>
    </div>
  )
}

BestPerformingAds.propTypes = {
  eventId: PropTypes.string.isRequired,
}

export default BestPerformingAds
